import React, { createContext, useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useNotification } from '@nginformatica/ngi-core'
import { TDesignVersion } from '@app/types'
import { UPDATE_EMPLOYEE_PREFERENCES } from '@core/graphql/mutations/employee-preferences'
import { EMPLOYEE_PREFERENCES } from '@core/graphql/queries/employee-preferences'

interface IEmployeePreferences {
    designVersion?: TDesignVersion | null
    expandedSidebar?: boolean | null
    favoritesOpen?: boolean | null
    favorites?: Array<{
        id: string
        label: string
        route: string
        iconType: string
    }> | null
}

interface IEmployeePreferencesQuery {
    employeeInfo: {
        preferences?: IEmployeePreferences | null
    } | null
}

interface IUpdateEmployeePreferencesMutation {
    updateEmployeePreferences: boolean
}

interface IUpdateEmployeePreferencesVariables {
    preferences: IEmployeePreferences
}

interface DesignVersionContextValue {
    version: TDesignVersion
    isV2: boolean
    loading: boolean
    initializing: boolean
    toggling: boolean
    toggle: () => Promise<void>
    expandedSidebar: boolean
    setExpandedSidebar: (value: boolean) => void
}

export const DesignVersionContext =
    createContext<DesignVersionContextValue | null>(null)

interface IProps {
    children: ReactNode
}

const DesignVersionProvider = ({ children }: IProps) => {
    const [notify] = useNotification()

    const { data, loading, error } = useQuery<IEmployeePreferencesQuery>(
        EMPLOYEE_PREFERENCES,
        {
            errorPolicy: 'all',
            fetchPolicy: 'cache-and-network'
        }
    )

    const [updatePreferences, { loading: toggling }] = useMutation<
        IUpdateEmployeePreferencesMutation,
        IUpdateEmployeePreferencesVariables
    >(UPDATE_EMPLOYEE_PREFERENCES, {
        refetchQueries: [{ query: EMPLOYEE_PREFERENCES }],
        awaitRefetchQueries: true
    })

    const version: TDesignVersion =
        data?.employeeInfo?.preferences?.designVersion ?? TDesignVersion.V2

    const initializing = !data && !error

    const expandedSidebar =
        data?.employeeInfo?.preferences?.expandedSidebar ?? true

    const setExpandedSidebar = useCallback(
        (value: boolean) => {
            updatePreferences({
                variables: { preferences: { expandedSidebar: value } }
            }).catch(() => {
                notify('error', 'Não foi possível salvar o estado do menu.')
            })
        },
        [updatePreferences, notify]
    )

    const toggle = useCallback(async () => {
        const next =
            version === TDesignVersion.V1
                ? TDesignVersion.V2
                : TDesignVersion.V1

        try {
            await updatePreferences({
                variables: { preferences: { designVersion: next } }
            })
        } catch {
            notify('error', 'Não foi possível alterar a versão do layout.')
        }
    }, [version, updatePreferences, notify])

    const value = useMemo(
        () => ({
            version,
            isV2: version === TDesignVersion.V2,
            loading,
            initializing,
            toggling,
            toggle,
            expandedSidebar,
            setExpandedSidebar
        }),
        [
            version,
            loading,
            initializing,
            toggling,
            toggle,
            expandedSidebar,
            setExpandedSidebar
        ]
    )

    return (
        <DesignVersionContext.Provider value={value}>
            {children}
        </DesignVersionContext.Provider>
    )
}

export default DesignVersionProvider
